import { useCallback, useState } from 'react';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { IbhDimensions } from '@hub/bearing-hub/common/types';
import type { IbhShape, IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import { ibhApiService } from '@hub/bearing-hub/common/api-service';
import type {
  IbhCustomArticleFileModelParams,
  IbhProductionMethods,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhGeoFileType } from '@igus/iglidur-bearing-hub-api/src/data-contracts';

type UseFetchThreeDimensionalModelProps = {
  articleNumber: string;
  session: Session;
  isCustom: boolean;
  shapeKey: IbhShape;
  dimensions: IbhDimensions;
  unitSystem: IbhUnitSystem;
  productionMethod: IbhProductionMethods;
};

export const useFetchThreeDimensionalModel = ({
  articleNumber,
  session,
  shapeKey,
  dimensions,
  isCustom,
  unitSystem,
}: UseFetchThreeDimensionalModelProps) => {
  const { sessionId } = session ?? {};
  const [modelLink, setModelLink] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  const fetchModelLink = useCallback(async () => {
    if (!articleNumber || !sessionId) {
      return;
    }

    try {
      const customParams = {
        articleNumber,
        fileType: IbhGeoFileType.Stl,
        shape: shapeKey,
        sid: sessionId,
        unitSystem,
        ...dimensions,
      } as IbhCustomArticleFileModelParams;

      const response = isCustom
        ? await ibhApiService.customArticleFileModel(customParams)
        : await ibhApiService.catalogArticleFileModel({ fileType: IbhGeoFileType.Stl, articleNumber });

      const blob = await response.blob();
      setModelLink(URL.createObjectURL(blob));
    } catch (err) {
      setModelLink(null);
      setError(`Failed to fetch 3D model`);
    }
  }, [articleNumber, session]);

  return { modelLink, error, fetchModelLink };
};
